import { faPowerOff } from '@fortawesome/pro-regular-svg-icons';
import { faCheckDouble } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useCallback, useState } from 'react';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';

export interface SERPTrackingDialogProps {
  project: ProjectGq;
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  reload: () => Promise<any>;
}

const SERPTrackingDialog: React.FC<SERPTrackingDialogProps> = ({
  project,
  open,
  reload,
  onClose,
}) => {
  // Mutation
  const useToggleSERPTracking = projectsApiService.mutations.serp.useToggleSERPTracking();
  const [loading, setLoading] = useState(false);

  const handleToggleSERPTracking = useCallback(() => {
    if (loading) return;

    // Local state
    setLoading(true);

    useToggleSERPTracking({
      projectId: project?.id,
      on: !project.serpTrackingOn?.length,
    })
      .then(reload)
      .then(() => setLoading(false))
      .then(() => onClose());
  }, [reload, project.id, loading]);

  const trackingIsOn = !!project.serpTrackingOn?.length;

  return (
    <VoxelDialog
      open={open}
      onClose={onClose}
      closeOnBackdrop
      title={`Rank tracking`}
      closeDisabled={loading}
    >
      <VoxelDialogContent>
        {trackingIsOn &&
          `The following action will turn off future keyword performance tracking for this project.`}

        {!trackingIsOn && (
          <div>
            <VoxelText variant="bread-normal" className="whitespace-pre-line">
              This will enable rank tracking for relevant keywords (unarchived with rank enabled)
            </VoxelText>
            <ul className="list-disc pl-3">
              <li>
                <VoxelText variant="bread-normal" paddingTop>
                  The checks will per default be performed on{' '}
                  <span className="font-semibold">Monday every week.</span>
                </VoxelText>
              </li>
              <li>
                <VoxelText variant="bread-normal" paddingTop>
                  Your existing data will be the starting point.
                </VoxelText>
              </li>
              <li>
                <VoxelText variant="bread-normal" paddingTop>
                  This uses your rank data capacity.
                </VoxelText>
              </li>
            </ul>
          </div>
        )}
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={onClose} disabled={loading} />
        <VoxelDialogActionSubmit
          title={trackingIsOn ? 'Turn off tracking' : 'Turn on tracking'}
          onClick={handleToggleSERPTracking}
          disabled={loading}
          endIcon={<FontAwesomeIcon icon={!project.serpTrackingOn ? faCheckDouble : faPowerOff} />}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default SERPTrackingDialog;
